import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldAlert } from "lucide-react";
import { SEO } from "../components/SEO";
import { useContactData } from "../hooks/useContactData";
import apiCall from "../utils/apiCall";

const inputClass =
  "w-full rounded-xl border border-border bg-background px-4 py-3 text-sm font-semibold text-foreground outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20";

export default function AccountDeletion() {
  const contactData = useContactData();
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    username: "",
    reason: "",
    confirm: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [requestId, setRequestId] = useState("");

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const response = await apiCall("/account-deletion", "POST", {
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.replace(/\D/g, "").slice(-10),
        username: form.username.trim(),
        reason: form.reason.trim(),
        confirm: form.confirm,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.success === false) {
        throw new Error(payload.message || "Could not submit the request.");
      }
      setRequestId(payload.data?.request_id || "");
    } catch (err) {
      setError(err.message || "Could not submit the request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Account Deletion Request"
        description="Request deletion of your OOMS account and associated personal data. Submit your registered details and we will process the request."
        canonicalUrl={`${contactData.websiteUrl}/account-deletion`}
        breadcrumbs={[
          { name: "Home", item: "/" },
          { name: "Account Deletion", item: "/account-deletion" },
        ]}
      />

      <div className="mx-auto max-w-3xl space-y-10 px-4 py-16 sm:px-6 lg:px-8">
        <section className="space-y-4 text-center">
          <span className="font-heading text-xs font-black uppercase tracking-[0.2em] text-primary-600 dark:text-primary-400">
            Privacy
          </span>
          <h1 className="font-heading text-4xl font-black tracking-tighter text-foreground sm:text-5xl">
            Request account deletion
          </h1>
          <p className="text-base font-medium leading-relaxed text-text-sub sm:text-lg">
            Use this page to ask OneSaaS to delete your OOMS account and the
            personal data tied to it. We review each request and confirm by
            email.
          </p>
        </section>

        <section className="space-y-4 rounded-[28px] border border-border bg-surface p-6 shadow-sm sm:p-8">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" />
            <div className="space-y-3 text-sm font-medium leading-relaxed text-text-sub">
              <p className="font-bold text-foreground">What happens after you submit</p>
              <ol className="list-decimal space-y-1.5 pl-4">
                <li>We match the details to the registered account.</li>
                <li>The account is closed and personal profile data is deleted, except records we must keep by law.</li>
                <li>Billing, tax, and fraud-prevention records may be retained for the period required by Indian law.</li>
                <li>You receive a status update on the email you enter below.</li>
              </ol>
              <p>
                Firm owners should export any business records they still need
                before requesting deletion. Questions can also go to{" "}
                <Link to="/contact" className="font-bold text-primary-600 hover:underline">
                  contact
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        {requestId ? (
          <section className="rounded-[28px] border border-emerald-200 bg-emerald-50 p-8 text-center dark:border-emerald-900 dark:bg-emerald-950/30">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
            <h2 className="mt-4 font-heading text-2xl font-black text-foreground">
              Request received
            </h2>
            <p className="mt-2 text-sm font-medium text-text-sub">
              Your reference is{" "}
              <span className="font-black text-foreground">{requestId}</span>.
              Keep it if you follow up with support.
            </p>
          </section>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-5 rounded-[28px] border border-border bg-surface p-6 shadow-xl shadow-foreground/5 sm:p-8"
          >
            <Field label="Full name">
              <input
                required
                type="text"
                autoComplete="name"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                className={inputClass}
                placeholder="Name on the account"
              />
            </Field>
            <Field label="Registered email">
              <input
                required
                type="text"
                inputMode="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                className={inputClass}
                placeholder="you@firm.com"
              />
            </Field>
            <Field label="Registered mobile">
              <input
                required
                type="text"
                inputMode="numeric"
                autoComplete="tel"
                value={form.mobile}
                onChange={(e) => setField("mobile", e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
                className={inputClass}
                placeholder="10-digit mobile"
              />
            </Field>
            <Field label="Username" hint="Optional. Helps us find the right account.">
              <input
                type="text"
                autoComplete="username"
                value={form.username}
                onChange={(e) => setField("username", e.target.value)}
                className={inputClass}
                placeholder="OOMS username"
              />
            </Field>
            <Field label="Reason" hint="Optional.">
              <textarea
                rows={4}
                value={form.reason}
                onChange={(e) => setField("reason", e.target.value.slice(0, 2000))}
                className={inputClass}
                placeholder="Tell us why you want the account deleted"
              />
            </Field>
            <label className="flex items-start gap-3 text-sm font-semibold text-foreground">
              <input
                type="checkbox"
                checked={form.confirm}
                onChange={(e) => setField("confirm", e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-border text-primary-600"
              />
              <span>
                I confirm that I want this OOMS account and the personal data
                associated with it deleted, subject to records we must retain
                by law.
              </span>
            </label>
            {error && (
              <p className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={submitting || !form.confirm}
              className="inline-flex w-full items-center justify-center rounded-xl bg-primary-600 px-5 py-3 text-sm font-black text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Submit deletion request"}
            </button>
          </form>
        )}
      </div>
    </>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-black uppercase tracking-wider text-text-sub">
        {label}
      </span>
      {children}
      {hint && <span className="block text-xs font-medium text-text-sub">{hint}</span>}
    </label>
  );
}
